import type { CSSProperties } from "react";
import type { EntityType } from "../canonical/model";
import { ENTITY_TYPES } from "../canonical/model";

/**
 * The LLC / Corporation selector at the top of the state panel.
 *
 * PERMANENT UI. Every entity type in `ENTITY_TYPES` is always rendered, on every
 * jurisdiction card, in every migration state. There is deliberately no prop
 * that removes a button, so "hide Corporation until its data is ready" is not
 * expressible here — the earlier `options` prop did exactly that and hid the
 * whole selector in practice.
 *
 * What readiness controls instead is `enabledOptions`. An entity type that is
 * not in that list renders disabled and readable, with `disabledTitle` as its
 * tooltip, and cannot be selected.
 *
 * Search is unaffected by design — the selector changes which entity's content a
 * jurisdiction shows, never how many jurisdictions exist. There is one row per
 * jurisdiction, not one per jurisdiction and entity type.
 */

export const ENTITY_LABELS: Record<EntityType, string> = {
  llc: "LLC",
  corp: "Corporation",
};

type Props = {
  value: EntityType;
  onChange: (next: EntityType) => void;
  /**
   * Entity types a CSR may actually pick. Defaults to both. Types absent from
   * this list still render — disabled, not hidden.
   */
  enabledOptions?: readonly EntityType[];
  /** Tooltip for a disabled option. CSR-facing wording only. */
  disabledTitle?: string;
};

/**
 * What a click on `picked` should do, given the current value and what is
 * enabled. `null` means "nothing happens".
 *
 * Extracted from the button handler so the selection rule is testable without a
 * DOM: a disabled option is inert, and re-picking the active option is a no-op.
 */
export function nextEntityOnPick(
  picked: EntityType,
  current: EntityType,
  enabledOptions: readonly EntityType[]
): EntityType | null {
  if (!enabledOptions.includes(picked)) return null;
  if (picked === current) return null;
  return picked;
}

const wrapStyle: CSSProperties = {
  display: "flex",
  gap: 4,
  padding: 3,
  marginBottom: 12,
  background: "rgba(0,0,0,0.35)",
  border: "1px solid rgba(255,255,255,0.10)",
  borderRadius: 10,
};

const optionStyle: CSSProperties = {
  flex: 1,
  background: "transparent",
  border: "1px solid transparent",
  color: "#fff",
  opacity: 0.7,
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: 0.6,
  textTransform: "uppercase",
  padding: "7px 10px",
  borderRadius: 8,
  cursor: "pointer",
  fontFamily: "inherit",
};

/** The active entity: neon-lit, unambiguously the one in effect. */
const selectedOptionStyle: CSSProperties = {
  ...optionStyle,
  background: "rgba(139,92,246,0.28)",
  borderColor: "rgba(165,180,252,0.55)",
  opacity: 1,
};

/**
 * Not selectable yet: dimmer than an inactive-but-available option and with a
 * not-allowed cursor, while staying legible — a CSR must be able to read that
 * Corporation exists as a concept, which is the point of showing it.
 */
const disabledOptionStyle: CSSProperties = {
  ...optionStyle,
  color: "rgba(255,255,255,0.62)",
  opacity: 1,
  cursor: "not-allowed",
};

/** A disabled button is not hit-tested, so the tooltip is carried by a wrapper. */
const disabledWrapStyle: CSSProperties = {
  flex: 1,
  display: "flex",
  cursor: "not-allowed",
};

export function EntitySelector({
  value,
  onChange,
  enabledOptions = ENTITY_TYPES,
  disabledTitle,
}: Props) {
  return (
    <div style={wrapStyle} role="group" aria-label="Entity type">
      {ENTITY_TYPES.map((entityType) => {
        const enabled = enabledOptions.includes(entityType);
        const selected = enabled && entityType === value;

        if (!enabled) {
          return (
            <span
              key={entityType}
              style={disabledWrapStyle}
              {...(disabledTitle ? { title: disabledTitle } : {})}
            >
              <button
                type="button"
                id={`entity-selector-${entityType}`}
                disabled
                aria-disabled="true"
                aria-pressed={false}
                {...(disabledTitle ? { title: disabledTitle } : {})}
                style={disabledOptionStyle}
              >
                {ENTITY_LABELS[entityType]}
              </button>
            </span>
          );
        }

        return (
          <button
            key={entityType}
            type="button"
            id={`entity-selector-${entityType}`}
            aria-pressed={selected}
            onClick={() => {
              const next = nextEntityOnPick(entityType, value, enabledOptions);
              if (next) onChange(next);
            }}
            style={selected ? selectedOptionStyle : optionStyle}
          >
            {ENTITY_LABELS[entityType]}
          </button>
        );
      })}
    </div>
  );
}
